import React from "react";

import { Edit } from "icons";

import { Button } from "clearsense-ui/components";
import PermissibleWrapper from "components/Common/PermissibleWrapper";
import { ColumnStyle, LinkText, Steward } from "components/Common/TableStyle";

const date = new Date();

export const CDE_COLUMNS = parent => [
  {
    dataIndex: "name",
    key: "name",
    fixed: "left",
    title: "Name",
    width: 200,
    render: (text, row) => (
      <LinkText
        text={text}
        link={`${row.id}/dsde`}
        state={{ cde: text, dataSource: parent?.name }}
      />
    ),
  },
  {
    dataIndex: "description",
    key: "description",
    title: "Description",
    render: text => <div className="whitespace-pre-line">{text}</div>,
    width: 300,
  },
  {
    dataIndex: "drafter",
    key: "drafter",
    title: "Drafter",
    render: row => <Steward name={row?.name} title={row?.role} />,
    width: 150,
  },
  {
    dataIndex: "approver",
    key: "approver",
    title: "Approver",
    render: row => <Steward name={row?.name} title={row?.role} />,
    width: 150,
  },
  {
    dataIndex: "is_secured",
    key: "is_secured",
    title: "Secured CDE",
    width: 150,
    render: row => {
      if (row === "") return "No";
      else if (row === "phi") return "PHI";

      return "Business Sensitive";
    },
  },
  {
    dataIndex: "mapping_status",
    key: "mapping_status",
    title: "Mapping Status",
    width: 150,
    render: text => <div className="whitespace-pre-line">{text}</div>,
  },
  {
    dataIndex: "data_source",
    key: "data_source",
    title: "# Data Source",
    width: 100,
    render: text => (text ? text : 0),
  },
];

export const DOMAINS_COLUMNS = [
  {
    title: "Name",
    key: "name",
    dataIndex: "name",
    width: "20%",
    render: text => <LinkText text={text} state={{ parent: text }} />,
  },
  {
    title: "Description",
    key: "description",
    dataIndex: "description",
    width: "30%",
    render: text => <ColumnStyle text={text} />,
  },
  {
    title: "Drafter",
    key: "drafter",
    dataIndex: "drafter",
    width: "20%",
    render: text => <Steward name={text?.name} title={text?.role} />,
  },
  {
    title: "Approver",
    key: "approver",
    dataIndex: "approver",
    width: "20%",
    render: text => <Steward name={text?.name} title={text?.role} />,
  },
  {
    title: "# CDE",
    key: "cde_count",
    dataIndex: "cde_count",
    width: "15%",
    render: text => <ColumnStyle text={text} />,
  },
  {
    title: "# Data Source",
    key: "data_source_count",
    dataIndex: "data_source_count",
    width: "30%",
    render: text => <ColumnStyle text={text} />,
  },
];

export const REFERENCE_SOURCES_COLUMNS = [
  {
    dataIndex: "name",
    key: "name",
    title: "Name",
    width: "20%",
  },
  {
    dataIndex: "description",
    key: "description",
    title: "Description",
    render: text => <div className="whitespace-pre-line">{text}</div>,
    width: "30%",
  },
  {
    dataIndex: "reference_source_type",
    key: "reference_source_type",
    title: "Type",
    render: text => <div className="whitespace-pre-line">{text}</div>,
    width: "20%",
  },
  {
    title: "Drafter",
    key: "drafter",
    dataIndex: "drafter",
    render: text => <Steward name={text?.name} title={text?.role} />,
    width: "15%",
  },
  {
    title: "Approver",
    key: "approver",
    dataIndex: "approver",
    render: text => <Steward name={text?.name} title={text?.role} />,
    width: "15%",
  },
];

export const ANALYTICS_REPORTS_COLUMNS = userPermissions => [
  {
    title: "Report name",
    key: "name",
    dataIndex: "name",
    render: text => <LinkText text={text} state={{ parent: text }} />,
  },
  {
    title: "Description",
    key: "description",
    dataIndex: "description",
    width: "45%",
    render: text => <ColumnStyle text={text} />,
  },
  {
    title: "Asset Type",
    key: "assetType",
    dataIndex: "assetType",
    render: text => <ColumnStyle text={text} />,
  },
  {
    title: "Owners",
    key: "steward",
    dataIndex: "steward",
    render: (text, record) => (
      <Steward name={text} title={record.title || "Data Steward"} />
    ),
  },
  {
    title: "Actions",
    key: "actions",
    dataIndex: "actions",
    render: () => (
      <PermissibleWrapper
        currentPermissions={userPermissions}
        acceptablePermissions="update"
      >
        <Button style="text" iconSize={24} icon={Edit} />
      </PermissibleWrapper>
    ),
  },
];

export const DATA_SOURCE_SAMPLE = [
  {
    id: 1,
    name: "Acuity",
    database: "acuity",
    steward: "Jordan Moyes",
    status: "Good",
    email: "jordan.moyas@company.com",
  },
  {
    id: 2,
    name: "Cerner",
    database: "cerner-ehr",
    steward: "Robert Fox",
    status: "In progress",
    email: "georgia.young@example.com",
  },
  {
    id: 3,
    name: "Provider DS",
    database: "provider-ds",
    steward: "Annette Black",
    status: "Failed",
    email: "kenzi.lawson@example.com",
  },
  {
    id: 4,
    name: "IDX",
    database: "idx",
    steward: "Esther Howard",
    status: "Good",
    email: "jackson.graham@example.com",
  },
  {
    id: 5,
    name: "Morrisey (MSOW & Apogee/Network Manager",
    database: "morrisey",
    steward: "Leslie Alexander",
    status: "Good",
    email: "michael.mitc@example.com",
  },
];

export const CDE_DATA = [
  {
    id: 1,
    name: "First Name",
    domain: "Provider",
    description: "A formal recognized healthcare organization",
    steward: "Tom Cook",
  },
  {
    id: 2,
    name: "Last Name",
    domain: "Provider",
    description:
      "Legally enforceable, formally recorded unilateral or bilateral directive i.e., a policy or agreement.",
    steward: "Tom Cook",
  },
  {
    id: 3,
    name: "NPI",
    domain: "Provider",
    description:
      "An interaction between a patient and healthcare provider(s) for the purpose of providing healthcare service(s) or assessing the health status of a patient.",
    steward: "Tom Cook",
  },
  {
    id: 4,
    name: "Sex",
    domain: "Provider",
    description:
      "A provider issued list of professional services and products provided to a patient which is sent to an insurer for reimbursement.",
    steward: "Tom Cook",
  },
];

export const DOMAINS_DATA = [
  {
    id: 1,
    name: "Provider A",
    description: "Provider's First Name",
    steward: "Tom Cook",
  },
  {
    id: 2,
    name: "Provider B",
    description: "Provider's Last Name",
    steward: "Tom Cook",
  },
  {
    id: 3,
    name: "Provider C",
    description: "National Provider ID",
    steward: "Tom Cook",
  },
  {
    id: 4,
    name: "Provider D",
    description: "National Provider ID",
    steward: "Tom Cook",
  },
  {
    id: 5,
    name: "Provider E",
    description: "National Provider ID",
    steward: "Tom Cook",
  },
];

export const REFERENCE_SOURCES_SAMPLE = [
  {
    id: 1,
    name: "Enterprise Gender",
    description: "This is our enterprise naming standards for gender",
    type: "Enterprise",
    steward: "Steward",
  },
  {
    id: 2,
    name: "HR Gender",
    description: "This is our HR department naming standards for gender",
    type: "Department",
    steward: "Steward",
  },
];

export const ANALYTICS_REPORTS_SAMPLE = [
  {
    key: 1,
    name: "CDE Report, June '22",
    description: "CDE report for June 2022",
    assetType: "CDE",
    steward: "Tom Cook",
  },
  {
    key: 2,
    name: "CDE Report, May '22",
    description: "CDE report for May 2022",
    assetType: "CDE",
    steward: "Tom Cook",
  },
  {
    key: 3,
    name: "DSDE Report",
    description: "DSDE reports consolidated",
    assetType: "CDE",
    steward: "Tom Cook",
  },
  {
    key: 4,
    name: "Domains Report",
    description: "Domains reports",
    assetType: "Domains",
    steward: "Tom Cook",
  },
];

export const DEFAULT_DATA_SOURCES_QUICK_FILTER_DATA = [
  {
    label: "Missing Descriptions",
    value: "",
    onClick: () => {},
  },
  {
    label: "Last 60 days",
    value: "",
    onClick: () => {},
  },
  {
    label: "Incomplete Mappings",
    value: "",
    onClick: () => {},
  },
];
export const DATA_SOURCES_QUICK_FILTER_DATA = (setFilter, dataSources) => [
  {
    label: "Missing Descriptions",
    value: dataSources?.missing_descriptions_count,
    onClick: () =>
      setFilter({
        filterBy: "description =",
        filterValue: "",
      }),
  },
  {
    label: "Last 60 days",
    value: dataSources?.created_in_last_60_days_count,
    onClick: () =>
      setFilter({
        filterBy: "created_at>",
        filterValue: new Date(date.setDate(date.getDate() - 60)).toUTCString(),
      }),
  },
  {
    label: "Incomplete Mappings",
    value: dataSources?.incomplete_mappings,
    onClick: () =>
      setFilter({
        filterBy: "incomplete_mappings",
        filterValue: "DataSource",
      }),
  },
];
